import { useEffect } from "react";
import { useLoadedAudioId } from "../../../domains/app/contexts/audio.context";
import { Trans, useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  IconChevronRight,
  IconSearch,
  IconSparkles,
} from "@tabler/icons-react";
import { useApiQuery } from "../../../services/api";
import { useOnScreen } from "../../../domains/app/hooks/useOnScreen";
import PlaylistCard from "../../../components/cards/PlaylistCard";
import { Button } from "../../../components/ui/Button";
import { JournalCard } from "../../../components/ui/JournalCard";
import { PublicationCard } from "../../../components/ui/PublicationCard";
import { useJournalFollow } from "../../../domains/journal/hooks/useJournalFollow";
import type { SearchJournalItem } from "../hooks/useSearchJournalsQuery";
import { usePageLinks } from "../../../domains/app/hooks/usePageLinks";
import { getArticleTypeVariant } from "../../../domains/app/utils/getArticleTypeText";
import { gaEventSearchResultClicked } from "../../../tools/analytics/domains/search.analytics";
import { useSearchArticlesQuery } from "../hooks/useSearchArticlesQuery";
import { useSearchJournalsQuery } from "../hooks/useSearchJournalsQuery";
import { useSearchPlaylistsQuery } from "../hooks/useSearchPlaylistsQuery";
import {
  useTrackSearchSectionSettled,
  type TrackSearchQuery,
} from "../hooks/useTrackSearchQuery";
import {
  useSearchRoomsQuery,
  type SearchRoomItem,
} from "../hooks/useSearchRoomsQuery";
import type { SearchCategory } from "../types";
import {
  AllLoadingSkeleton,
  ArticleSkeletonRow,
  ArticlesLoadingSkeleton,
  JournalSkeletonRow,
  JournalsLoadingSkeleton,
  PlaylistSkeletonRow,
  PlaylistsLoadingSkeleton,
  RoomsLoadingSkeleton,
} from "./SearchSkeletons";
import { SearchKeyFacts } from "./SearchKeyFacts";
import { SearchTopRoomCard } from "./SearchTopRoomCard";
import { getRoomName } from "../../../domains/room/hooks/useRoomName";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { formatArticleDate } from "../../../domains/app/utils/renderDate";
import { authorLine } from "../../../domains/article/utils/authorLine";

export function SearchResults({
  searchValue,
  roomIdForQuery,
  category,
  scope,
  fireResultsSettled,
}: {
  searchValue: string;
  /** roomId actually sent to the API (null when scope is global). */
  roomIdForQuery: string | null;
  category: SearchCategory;
  scope: "global" | "room";
  fireResultsSettled: TrackSearchQuery["fireResultsSettled"];
}): React.JSX.Element {
  switch (category) {
    case "all":
      return (
        <AllSection
          searchValue={searchValue}
          roomIdForQuery={roomIdForQuery}
          scope={scope}
          fireResultsSettled={fireResultsSettled}
        />
      );
    case "articles":
      return (
        <ArticlesSection
          searchValue={searchValue}
          roomIdForQuery={roomIdForQuery}
          scope={scope}
          fireResultsSettled={fireResultsSettled}
        />
      );
    case "rooms":
      return (
        <RoomsSection
          searchValue={searchValue}
          roomIdForQuery={roomIdForQuery}
          scope={scope}
          fireResultsSettled={fireResultsSettled}
        />
      );
    case "journals":
      return (
        <JournalsSection
          searchValue={searchValue}
          roomIdForQuery={roomIdForQuery}
          scope={scope}
          fireResultsSettled={fireResultsSettled}
        />
      );
    case "playlists":
      return (
        <PlaylistsSection
          searchValue={searchValue}
          roomIdForQuery={roomIdForQuery}
          scope={scope}
          fireResultsSettled={fireResultsSettled}
        />
      );
    default:
      throw new Error(category satisfies never);
  }
}

function AllSection({
  searchValue,
  roomIdForQuery,
  scope,
  fireResultsSettled,
}: {
  searchValue: string;
  roomIdForQuery: string | null;
  scope: "global" | "room";
  fireResultsSettled: TrackSearchQuery["fireResultsSettled"];
}): React.JSX.Element {
  const articles = useSearchArticlesQuery(searchValue, roomIdForQuery);
  const rooms = useSearchRoomsQuery(searchValue);
  const topRooms = rooms.rooms.slice(0, 5);

  useTrackSearchSectionSettled({
    isLoading: articles.isLoading || rooms.isLoading,
    fire: fireResultsSettled,
    term: searchValue,
    category: "all",
    count: articles.total + rooms.total,
    scope,
  });

  if (articles.isLoading)
    return (
      <AllLoadingSkeleton
        withTopRoom={rooms.isLoading || topRooms.length > 0}
      />
    );

  return (
    <div className="flex w-full flex-col gap-4">
      {topRooms.length > 0 && (
        <div className="flex w-full flex-col gap-2">
          {topRooms.map((room, index) => (
            <SearchTopRoomCard
              key={room.id}
              room={room}
              onVisit={() => {
                gaEventSearchResultClicked({
                  term: searchValue,
                  category: "all",
                  resultType: "room",
                  resultId: room.id,
                  position: index,
                  scope,
                });
              }}
            />
          ))}
        </div>
      )}
      <ArticlesList
        searchValue={searchValue}
        roomIdForQuery={roomIdForQuery}
        category="all"
        scope={scope}
        articles={articles.articles}
        total={articles.total}
        isLoading={articles.isLoading}
        isFetching={articles.isFetching}
        hasNextPage={articles.hasNextPage}
        fetchNextPage={articles.fetchNextPage}
      />
    </div>
  );
}

function ArticlesSection({
  searchValue,
  roomIdForQuery,
  scope,
  fireResultsSettled,
}: {
  searchValue: string;
  roomIdForQuery: string | null;
  scope: "global" | "room";
  fireResultsSettled: TrackSearchQuery["fireResultsSettled"];
}): React.JSX.Element {
  const articles = useSearchArticlesQuery(searchValue, roomIdForQuery);
  useTrackSearchSectionSettled({
    isLoading: articles.isLoading,
    fire: fireResultsSettled,
    term: searchValue,
    category: "articles",
    count: articles.total,
    scope,
  });
  return (
    <ArticlesList
      searchValue={searchValue}
      roomIdForQuery={roomIdForQuery}
      category="articles"
      scope={scope}
      articles={articles.articles}
      total={articles.total}
      isLoading={articles.isLoading}
      isFetching={articles.isFetching}
      hasNextPage={articles.hasNextPage}
      fetchNextPage={articles.fetchNextPage}
    />
  );
}

function ArticlesList({
  searchValue,
  roomIdForQuery,
  category,
  scope,
  articles,
  total,
  isLoading,
  isFetching,
  hasNextPage,
  fetchNextPage,
}: {
  searchValue: string;
  roomIdForQuery: string | null;
  category: "all" | "articles";
  scope: "global" | "room";
  articles: ReturnType<typeof useSearchArticlesQuery>["articles"];
  total: number;
  isLoading: boolean;
  isFetching: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
}): React.JSX.Element {
  const loadedAudioId = useLoadedAudioId();
  const { t } = useTranslation();
  const history = useHistory();
  const { articleLink } = usePageLinks(null);
  if (isLoading) return <ArticlesLoadingSkeleton />;
  if (articles.length === 0)
    return (
      <NoResultsState
        searchValue={searchValue}
        roomIdForQuery={roomIdForQuery}
      />
    );
  const articleIds = articles.map((a) => a.id);
  return (
    <div className="flex w-full flex-col gap-2">
      <SearchKeyFacts searchValue={searchValue} articleIds={articleIds} />
      <ResultCount label={t("{{count}} results found", { count: total })} />
      <ul className="flex w-full flex-col divide-y divide-ds-black/6">
        {articles.map((article, index) => {
          const journal = article.journal;
          return (
            <li
              key={article.id}
              className="w-full animate-fade-in-up py-4 [animation-fill-mode:backwards] first:pt-0 last:pb-0"
            >
              <PublicationCard
                isInPlayer={loadedAudioId === article.id}
                title={article.title}
                journalName={journal === null ? "" : journal.name}
                author={authorLine(article.primary_author, article.authors)}
                date={formatArticleDate(article.publication_date, "en-US")}
                meta={
                  article.article_type === null
                    ? "Article"
                    : article.article_type
                }
                metaVariant={getArticleTypeVariant(article.article_type)}
                access={article.isOpenAccess === true ? "open" : "closed"}
                imageUrl={null}
                description={null}
                recommendation={null}
                isNew={false}
                isBookmarked={false}
                onBookmark={null}
                onShare={null}
                onClick={() => {
                  gaEventSearchResultClicked({
                    term: searchValue,
                    category,
                    resultType: "article",
                    resultId: article.id,
                    position: index,
                    scope,
                  });
                  history.push(articleLink(article));
                }}
                onJournalClick={
                  journal === null
                    ? null
                    : () => {
                        history.push(`/journal/${journal.uid}`);
                      }
                }
                onRecommendationClick={null}
              />
            </li>
          );
        })}
      </ul>
      {hasNextPage && (
        <InfiniteScrollSentinel
          enabled={!isFetching}
          isFetching={isFetching}
          onReached={fetchNextPage}
          loader={
            <div className="flex w-full flex-col divide-y divide-ds-black/6 border-t border-ds-black/6">
              <ArticleSkeletonRow />
              <ArticleSkeletonRow />
              <ArticleSkeletonRow />
            </div>
          }
        />
      )}
    </div>
  );
}

function RoomsSection({
  searchValue,
  roomIdForQuery,
  scope,
  fireResultsSettled,
}: {
  searchValue: string;
  roomIdForQuery: string | null;
  scope: "global" | "room";
  fireResultsSettled: TrackSearchQuery["fireResultsSettled"];
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const rooms = useSearchRoomsQuery(searchValue);
  useTrackSearchSectionSettled({
    isLoading: rooms.isLoading,
    fire: fireResultsSettled,
    term: searchValue,
    category: "rooms",
    count: rooms.total,
    scope,
  });
  if (rooms.isLoading) return <RoomsLoadingSkeleton />;
  if (rooms.rooms.length === 0)
    return (
      <NoResultsState
        searchValue={searchValue}
        roomIdForQuery={roomIdForQuery}
      />
    );
  return (
    <div className="flex w-full flex-col gap-2">
      <ResultCount label={t("{{count}} rooms found", { count: rooms.total })} />
      {rooms.rooms.map((room, index) => (
        <RoomRow
          key={room.id}
          room={room}
          onClick={() => {
            gaEventSearchResultClicked({
              term: searchValue,
              category: "rooms",
              resultType: "room",
              resultId: room.id,
              position: index,
              scope,
            });
            history.push(`/room/${room.id}`);
          }}
        />
      ))}
    </div>
  );
}

function JournalsSection({
  searchValue,
  roomIdForQuery,
  scope,
  fireResultsSettled,
}: {
  searchValue: string;
  roomIdForQuery: string | null;
  scope: "global" | "room";
  fireResultsSettled: TrackSearchQuery["fireResultsSettled"];
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const result = useSearchJournalsQuery(searchValue);
  useTrackSearchSectionSettled({
    isLoading: result.isLoading,
    fire: fireResultsSettled,
    term: searchValue,
    category: "journals",
    count: result.total,
    scope,
  });
  if (result.isLoading) return <JournalsLoadingSkeleton />;
  if (result.journals.length === 0)
    return (
      <NoResultsState
        searchValue={searchValue}
        roomIdForQuery={roomIdForQuery}
      />
    );
  return (
    <div className="flex w-full flex-col gap-2">
      <ResultCount
        label={t("{{count}} journals found", { count: result.total })}
      />
      <ul className="flex w-full flex-col">
        {result.journals.map((journal, index) => (
          <li key={journal.id}>
            <SearchJournalRow
              journal={journal}
              isLast={index === result.journals.length - 1}
              onClick={() => {
                gaEventSearchResultClicked({
                  term: searchValue,
                  category: "journals",
                  resultType: "journal",
                  resultId: journal.id,
                  position: index,
                  scope,
                });
                history.push(`/journal/${journal.uid}`);
              }}
            />
          </li>
        ))}
      </ul>
      {result.hasNextPage && (
        <InfiniteScrollSentinel
          enabled={!result.isFetching}
          isFetching={result.isFetching}
          onReached={result.fetchNextPage}
          loader={
            <div className="flex w-full flex-col gap-2 pt-2">
              <JournalSkeletonRow />
              <JournalSkeletonRow />
              <JournalSkeletonRow />
            </div>
          }
        />
      )}
    </div>
  );
}

function SearchJournalRow({
  journal,
  isLast,
  onClick,
}: {
  journal: SearchJournalItem;
  isLast: boolean;
  onClick: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { isFollowed, toggleFollow } = useJournalFollow(journal);

  return (
    <JournalCard
      title={journal.name}
      isLast={isLast}
      onClick={onClick}
      action={
        <Button
          variant={isFollowed ? "outline" : "primary"}
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            toggleFollow(journal);
          }}
        >
          {isFollowed ? t("Unfollow") : t("Follow")}
        </Button>
      }
    />
  );
}

function PlaylistsSection({
  searchValue,
  roomIdForQuery,
  scope,
  fireResultsSettled,
}: {
  searchValue: string;
  roomIdForQuery: string | null;
  scope: "global" | "room";
  fireResultsSettled: TrackSearchQuery["fireResultsSettled"];
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { publicPlaylistLink } = usePageLinks(null);
  const result = useSearchPlaylistsQuery(searchValue);
  useTrackSearchSectionSettled({
    isLoading: result.isLoading,
    fire: fireResultsSettled,
    term: searchValue,
    category: "playlists",
    count: result.total,
    scope,
  });
  if (result.isLoading) return <PlaylistsLoadingSkeleton />;
  if (result.playlists.length === 0)
    return (
      <NoResultsState
        searchValue={searchValue}
        roomIdForQuery={roomIdForQuery}
      />
    );
  return (
    <div className="flex w-full flex-col gap-2">
      <ResultCount
        label={t("{{count}} playlists found", { count: result.total })}
      />
      <ul className="flex w-full flex-col divide-y divide-ds-black/6">
        {result.playlists.map((playlist, index) => (
          <li key={playlist.id} className="py-3 first:pt-0 last:pb-0">
            <PlaylistCard
              variant="light"
              playlist={{
                id: playlist.id,
                title: playlist.title,
                headerTitle: playlist.headerTitle,
                images: playlist.images.map((url) => ({ url, sizes: null })),
                metrics: { articles: playlist.articleCount },
                room: playlist.room,
              }}
              onClick={() => {
                gaEventSearchResultClicked({
                  term: searchValue,
                  category: "playlists",
                  resultType: "playlist",
                  resultId: playlist.id,
                  position: index,
                  scope,
                });
                history.push(publicPlaylistLink({ id: playlist.id }));
              }}
            />
          </li>
        ))}
      </ul>
      {result.hasNextPage && (
        <InfiniteScrollSentinel
          enabled={!result.isFetching}
          isFetching={result.isFetching}
          onReached={result.fetchNextPage}
          loader={
            <div className="flex w-full flex-col divide-y divide-ds-black/6 border-t border-ds-black/6 *:py-3">
              <PlaylistSkeletonRow />
              <PlaylistSkeletonRow />
              <PlaylistSkeletonRow />
            </div>
          }
        />
      )}
    </div>
  );
}

function RoomRow({
  room,
  onClick,
}: {
  room: SearchRoomItem;
  onClick: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();
  const roomName = getRoomName(room, activeLang);
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 border-b border-ds-black/6 py-3 text-left"
    >
      {room.icoModel === null ? (
        <div className="flex size-10 shrink-0 items-center justify-center rounded-2xl bg-ds-slate-100 text-sm font-bold text-ds-black">
          {roomName.slice(0, 1).toUpperCase()}
        </div>
      ) : (
        <img
          src={
            room.icoModel.sizes !== null
              ? room.icoModel.sizes.icon.file
              : room.icoModel.url
          }
          alt=""
          className="size-10 shrink-0 rounded-2xl object-cover"
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold text-ds-black">
          {roomName}
        </span>
        {room.organisationModel !== null && (
          <span className="truncate text-xs text-ds-slate-700">
            {t("by {{name}}", { name: room.organisationModel.name })}
          </span>
        )}
      </div>
    </button>
  );
}

function ResultCount({ label }: { label: string }): React.JSX.Element {
  return (
    <div className="pb-1 text-xs font-semibold uppercase tracking-tight text-ds-slate-400">
      {label}
    </div>
  );
}

function InfiniteScrollSentinel({
  enabled,
  isFetching,
  onReached,
  loader,
}: {
  enabled: boolean;
  isFetching: boolean;
  onReached: () => void;
  loader: React.ReactNode;
}): React.JSX.Element {
  const { ref, isIntersecting } = useOnScreen();
  useEffect(() => {
    if (enabled && isIntersecting) onReached();
  }, [enabled, isIntersecting, onReached]);
  return (
    <div ref={ref} className="w-full">
      {isFetching ? loader : null}
    </div>
  );
}

function NoResultsState({
  searchValue,
  roomIdForQuery,
}: {
  searchValue: string;
  roomIdForQuery: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();
  const history = useHistory();
  const searchRooms = useSearchRoomsQuery(searchValue);
  const publicRoomsQuery = useApiQuery("/webapp/public/rooms", {
    durable: true,
  });
  const publicRooms =
    publicRoomsQuery.state === "success" ? publicRoomsQuery.body : [];
  const searchRoomMatch = searchRooms.rooms.find(
    (room) => room.id !== roomIdForQuery
  );
  const publicRoomMatch = publicRooms.find(
    (room) => room.id !== roomIdForQuery
  );
  const adjacentRoom =
    searchRoomMatch !== undefined
      ? searchRoomMatch
      : publicRoomMatch !== undefined
        ? publicRoomMatch
        : null;
  const isRoomScope = roomIdForQuery !== null;

  return (
    <div className="flex w-full flex-col items-center gap-4 py-12">
      <div className="flex flex-col items-center gap-4">
        <div className="flex size-12 items-center justify-center rounded-full bg-ds-mint-50">
          <IconSearch className="size-6 text-ds-slate-700" />
        </div>
        <p className="text-center text-2xl font-semibold tracking-tight text-primary-black">
          {isRoomScope
            ? t("No facts to surface from your room")
            : t("No results found")}
        </p>
        <p className="text-center text-sm/snug  text-secondary-bluegrey-4">
          {isRoomScope ? (
            t(
              "Not enough sources in your Rooms to synthesize. Try a broader scope:"
            )
          ) : (
            <Trans
              i18nKey="We couldn't find anything matching <strong>{{searchValue}}</strong>."
              values={{ searchValue }}
              components={{ strong: <span className="font-medium" /> }}
            />
          )}
        </p>
      </div>
      <div className="flex w-full flex-col gap-3">
        {adjacentRoom !== null && (
          <SuggestionCard
            badge={t("Adjacent Room")}
            title={getRoomName(adjacentRoom, activeLang)}
            subtitle={
              adjacentRoom.organisationModel === null
                ? t("Curated by Juisci")
                : t("by {{name}} · curated by Juisci", {
                    name: adjacentRoom.organisationModel.name,
                  })
            }
            tone="neutral"
            icon={
              adjacentRoom.icoModel === null ? (
                <div className="flex size-full items-center justify-center rounded-xl bg-ds-slate-100 text-sm font-bold text-ds-black">
                  {getRoomName(adjacentRoom, activeLang)
                    .slice(0, 1)
                    .toUpperCase()}
                </div>
              ) : (
                <img
                  src={
                    adjacentRoom.icoModel.sizes !== null
                      ? adjacentRoom.icoModel.sizes.icon.file
                      : adjacentRoom.icoModel.url
                  }
                  alt=""
                  className="size-full rounded-xl object-cover"
                />
              )
            }
            onClick={() => {
              history.push(`/room/${adjacentRoom.id}`);
            }}
          />
        )}
        <SuggestionCard
          badge={t("AI Synthesis")}
          title={t("Ask JuisciGPT")}
          subtitle={t("Get an AI-generated synthesis from outside your Rooms")}
          tone="lime"
          icon={<IconSparkles className="size-5 text-ds-black" />}
          onClick={() => {
            history.push("/gpt/chat/new");
          }}
        />
      </div>
    </div>
  );
}

function SuggestionCard({
  badge,
  title,
  subtitle,
  tone,
  icon,
  onClick,
}: {
  badge: string;
  title: string;
  subtitle: string;
  tone: "neutral" | "lime";
  icon: React.ReactNode;
  onClick: () => void;
}): React.JSX.Element {
  const containerClass =
    tone === "lime"
      ? "border-secondary-soft-yellow-2 bg-secondary-soft-yellow-1"
      : "border-ds-slate-200 bg-white";
  const iconWrapperClass =
    tone === "lime" ? "bg-ds-lime-100" : "bg-ds-slate-100";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-2xl border px-3.5 py-3 text-left ${containerClass}`}
    >
      <div
        className={`flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-xl ${iconWrapperClass}`}
      >
        {icon}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 overflow-hidden">
        <p className="text-[11px] font-semibold uppercase tracking-tight text-secondary-bluegrey-4">
          {badge}
        </p>
        <p className="truncate text-sm font-medium text-primary-black">
          {title}
        </p>
        <p className="truncate text-[11px] text-secondary-bluegrey-4">
          {subtitle}
        </p>
      </div>
      <IconChevronRight className="size-4 shrink-0 text-secondary-bluegrey-4" />
    </button>
  );
}
